import useDebouncedAction from "@/hooks/useDebouncedAction";
import useCompanies from "@/hooks/useCompanies";
import useToastStore from "@/services/store/useToastStore";
import { authenticationLocalStorage } from "@/utils/constants/localStorage";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { GridPaginationModel } from "@mui/x-data-grid";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import CompanyTable from "./components/CompanyTable";
import CreateCompanyPopup from "./components/CreateCompanyPopup";
import DeleteCompanyDialog from "./components/DeleteCompanyDialog";

const PAGE_SIZE = 10;

const Admin: FC = () => {
  const navigate = useNavigate();
  const { companies, totalElements, getCompanies, createCompany, deleteCompany } = useCompanies();
  const { setToast } = useToastStore();
  const ownCompanyId = authenticationLocalStorage.loggedUser?.companyId;

  const [nameFilter, setNameFilter] = useState("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CompanyDTO | null>(null);

  const fetchCompanies = () => {
    setLoading(true);
    getCompanies({
      page: paginationModel.page,
      size: paginationModel.pageSize,
      name: nameFilter || undefined,
    }).finally(() => setLoading(false));
  };

  useDebouncedAction([nameFilter, paginationModel.page, paginationModel.pageSize], fetchCompanies);

  const handleCreate = (name: string): Promise<void> =>
    createCompany(name)
      .then(() => {
        setCreateOpen(false);
        fetchCompanies();
      })
      .catch(() => {
        setToast({ isOpen: true, type: "error", message: "Erreur lors de la création de l'entreprise." });
      });

  const handleDelete = (): Promise<void> => {
    if (!deleteTarget) return Promise.resolve();
    return deleteCompany(deleteTarget.id)
      .then(() => {
        setDeleteTarget(null);
        fetchCompanies();
      })
      .catch((error) => {
        const message = error?.response?.data?.message ?? "Erreur lors de la suppression de l'entreprise.";
        setToast({ isOpen: true, type: "error", message });
      });
  };

  return (
    <Box sx={{ padding: 3, boxSizing: "border-box", height: "100%" }}>
      <Stack spacing={2} height="100%">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h5">Entreprises</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Nouvelle entreprise
          </Button>
        </Stack>

        <TextField
          label="Rechercher par nom"
          variant="outlined"
          size="small"
          value={nameFilter}
          onChange={(e) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            setNameFilter(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />

        <CompanyTable
          companies={companies}
          totalElements={totalElements}
          loading={loading}
          paginationModel={paginationModel}
          ownCompanyId={ownCompanyId}
          onPaginationModelChange={setPaginationModel}
          onRowClick={(company) => navigate(`/admin/company/${company.id}`)}
          onDeleteClick={setDeleteTarget}
        />
      </Stack>

      <CreateCompanyPopup open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreate} />

      <DeleteCompanyDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} />
    </Box>
  );
};

export default Admin;
